import React, { Suspense } from 'react';
import './index.css';
import '@fontsource/roboto/latin-300.css';
import '@fontsource/roboto/latin-400.css';
import '@fontsource/roboto/latin-500.css';
import '@fontsource/roboto/latin-700.css';
import { registerSW } from 'virtual:pwa-register';
import { isNativeApp } from './services/app';
import * as pwa from './services/pwa';
import { getVersion } from './services/version';

/**
 * On-screen developer console for debugging on devices without a desktop browser
 * (e.g. an iPad without a Mac for Safari Web Inspector). Opt-in only:
 *   - open the app with `?debug=1` to enable it (the choice is remembered),
 *   - open with `?debug=0` to disable it again.
 * When enabled it loads eruda (a tap-to-open DevTools overlay: console, network, DOM)
 * from a CDN, so it adds nothing to the production bundle and never shows by default.
 */
const initDebugConsole = (): void => {
    try {
        const debug = new URLSearchParams(window.location.search).get('debug');
        if (debug === '1') {
            window.localStorage.setItem('eruda', '1');
        } else if (debug === '0') {
            window.localStorage.removeItem('eruda');
        }
        if (window.localStorage.getItem('eruda') !== '1') {
            return;
        }

        // Capture errors and WebGL context events SYNCHRONOUSLY, before eruda loads from the
        // CDN, so nothing that happens early is lost. eruda only records console calls made
        // after it inits, and a lost WebGL context is a DOM event (not a console error), so
        // without this the most likely cause of the black stage would never be visible.
        const buffer: string[] = [];
        const note = (message: string) => {
            buffer.push(message);

            console.error(message);
        };
        window.addEventListener('error', (e) => {
            note(`[error] ${e.message} @ ${e.filename}:${e.lineno}:${e.colno}`);
        });
        window.addEventListener('unhandledrejection', (e) => {
            note(`[promise] ${(e.reason && (e.reason.stack || e.reason.message)) || e.reason}`);
        });
        // capture phase (3rd arg true) so these fire even though the events don't bubble
        window.addEventListener(
            'webglcontextlost',
            (e) => note(`[webgl] context LOST on ${(e.target as Element)?.tagName}`),
            true
        );
        window.addEventListener(
            'webglcontextcreationerror',
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (e) => note(`[webgl] context creation FAILED: ${(e as any).statusMessage}`),
            true
        );

        const script = document.createElement('script');
        script.src = 'https://cdn.jsdelivr.net/npm/eruda';
        script.onload = () => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const eruda = (window as any).eruda;
            if (eruda == null) {
                return;
            }
            eruda.init();
            // replay anything captured before eruda was ready
            buffer.forEach((message) => console.error(`(buffered) ${message}`));
        };
        document.head.appendChild(script);
    } catch {
        // never let the debug helper break app start-up
    }
};
initDebugConsole();

const renderApp = async (): Promise<void> => {
    // Load runtime-editable settings (build/config/settings.json) BEFORE the store
    // modules evaluate — they read settings at module load, and Provider is imported
    // dynamically below, so awaiting here guarantees the values are in place.
    const { loadSettings } = await import('./config/settings');
    await loadSettings();

    const { createRoot } = await import('react-dom/client');
    const Provider = React.lazy(() => import('./components/Provider'));

    createRoot(document.getElementById('root') as HTMLElement).render(
        <React.StrictMode>
            <Suspense fallback={null}>
                <Provider />
            </Suspense>
        </React.StrictMode>
    );
};

if (isNativeApp()) {
    // The native app ships its files, so no service worker and no install prompt.
    renderApp();
} else {
    // Version before a confirmed update, so the reloaded app can confirm that it was updated.
    const UPDATED_FROM_KEY = 'pwa-updated-from';

    // The new service worker waits until every tab / the installed app is closed; a plain reload
    // keeps the old version. updateSW() activates it right away and reloads the page.
    const updateSW = registerSW({
        onNeedRefresh: () => {
            if (
                window.confirm('Eine neue Version ist verfügbar. Jetzt aktualisieren? Die App wird dabei neu geladen.')
            ) {
                try {
                    window.sessionStorage.setItem(UPDATED_FROM_KEY, getVersion());
                } catch {
                    // Without storage there is just no confirmation after the reload.
                }
                updateSW();
            }
        }
    });

    pwa.register();
    renderApp();

    try {
        const updatedFrom = window.sessionStorage.getItem(UPDATED_FROM_KEY);
        if (updatedFrom != null) {
            window.sessionStorage.removeItem(UPDATED_FROM_KEY);
            if (updatedFrom !== getVersion()) {
                // After the first render, so the app isn't blocked by the dialog while loading.
                window.setTimeout(() => {
                    alert(`Die App wurde auf Version ${getVersion()} aktualisiert.`);
                }, 1000);
            }
        }
    } catch {
        // Storage not available (e.g. blocked): skip the confirmation.
    }
}
